import { z } from "zod";
import { type PublicTracking, bookingTrackingSchema } from "./marketing";
import type { PaymentSession } from "./payments";
import type { DepositRule } from "./settings";
import { localDateTimeSchema, oneService, type PriceLine, visitServiceSchema } from "./booking";
import { type ConsentChannel, normalizePhone } from "./clients";
import { mobileAddressSchema } from "./mobile-events";

/**
 * Online booking (clients, no staff sign-in): /book/<clinic>.
 * Clients sign in with a code texted to their mobile; everyone on that
 * number (and the minors they're guardian for) is their household.
 */

/** The exact wording shown next to each opt-in. Stored with the consent so we can show what was agreed to. */
export const ONLINE_CONSENT_WORDING: Record<ConsentChannel, string> = {
  sms_reminders: "Text me appointment confirmations and reminders. Message and data rates may apply. Reply STOP to opt out.",
  email_reminders: "Email me appointment confirmations and reminders.",
  sms_marketing: "Text me offers and news. Up to 4 messages a month. Message and data rates may apply. Reply STOP to opt out.",
  email_marketing: "Email me offers and news. Unsubscribe any time.",
};

/** The booking page's steps, in order, for counting where people stop. */
export const BOOKING_STEPS = ["start", "home", "where", "address", "category", "service", "who", "time", "phone", "code", "pick-me", "about", "review", "done"] as const;
export type BookingStep = (typeof BOOKING_STEPS)[number];

/** A step reached on the booking page (nothing personal: what, how many, which kind of device). */
export const bookingAttemptSchema = z.object({
  id: z.string().uuid(),
  step: z.enum(BOOKING_STEPS),
  context: z.enum(["clinic", "mobile", "event"]).default("clinic"),
  serviceIds: z.array(z.string().uuid()).max(20).default([]),
  guests: z.number().int().min(1).max(20).default(1),
  device: z.enum(["phone", "tablet", "desktop"]).default("desktop"),
  /** The day and time picked so far (clinic-local), if any. */
  start: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/).nullable().default(null),
});

/** Reports → Unfinished bookings: where people stop, and who to follow up with. */
export interface UnfinishedBookingsReport {
  from: string;
  to: string;
  /** How many tries reached each stage, this period and the one before (same length). */
  funnel: { key: string; label: string; count: number; previous: number }[];
  /** Signed in (so we know who) but didn't book, newest first. */
  unfinished: {
    id: string;
    at: string;
    stoppedAt: string;
    /** The day and time they picked (clinic-local "YYYY-MM-DDTHH:MM"), if they got that far. */
    start: string | null;
    /** They reached the card or deposit step, and the time was held, then let go. */
    letGo: boolean;
    context: string;
    services: string;
    guests: number;
    device: string;
    phone: string;
    client: { id: string; name: string; canText: boolean } | null;
  }[];
}

/** What a booker ticks to book for a child. Kept with the booking (audit log). */
export const GUARDIAN_WORDING = "I'm their parent or legal guardian.";

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use a date like 1990-04-02");
const name = z.string().trim().min(1, "Required").max(80);
const mobile = z
  .string()
  .trim()
  .transform((v) => normalizePhone(v))
  .pipe(z.string({ message: "Enter a 10-digit mobile number" }));

export const sendCodeSchema = z.object({ phone: mobile });
export const verifyCodeSchema = z.object({
  phone: mobile,
  code: z.string().trim().regex(/^\d{6}$/, "Enter the 6-digit code"),
  /** "Remember this phone": stay signed in for booking for 30 days instead of 2 hours. */
  remember: z.boolean().default(false),
});

export const onlinePersonSchema = z.object({
  firstName: name,
  lastName: name,
  dob: isoDate,
  /** Required for adults (checked when booking); minors can leave it empty. */
  email: z
    .string()
    .trim()
    .toLowerCase()
    .max(254)
    .nullable()
    .default(null)
    .transform((v) => (v ? v : null))
    .pipe(z.string().email("Enter an email address").nullable()),
  /** A guest's own mobile, so they get their own texts (required for adults, checked when booking). */
  mobile: z
    .string()
    .trim()
    .max(30)
    .nullable()
    .default(null)
    .transform((v) => (v ? normalizePhone(v) : null)),
  /** Minors: the booker ticked "I'm their parent or legal guardian". */
  guardian: z.boolean().default(false),
  /** Adults: link them to the booker as family, so they're one tap next time. */
  saveAsFamily: z.boolean().default(false),
});

export const onlineNewClientSchema = onlinePersonSchema.extend({
  email: z.string().trim().toLowerCase().email("Enter an email address").max(254),
  consents: z
    .object({
      sms_reminders: z.boolean().default(false),
      email_reminders: z.boolean().default(false),
      sms_marketing: z.boolean().default(false),
      email_marketing: z.boolean().default(false),
    } satisfies Record<ConsentChannel, unknown>)
    .default({ sms_reminders: false, email_reminders: false, sms_marketing: false, email_marketing: false }),
});
export type OnlineNewClient = z.infer<typeof onlineNewClientSchema>;

/** A guest's services (as many as they like), each with its add-ons. */
const onlineService = z.object({
  services: z.array(visitServiceSchema).min(1, "Pick a service").max(8),
  /** Only honored when the clinic lets clients choose their nurse. */
  staffId: z.string().uuid().nullable().default(null),
});

const visitContext = z.enum(["clinic", "mobile", "event"]).default("clinic");

export const onlineSlotsSchema = z.object({
  date: isoDate,
  /** clientId (when known) lets prices show membership vouchers and member pricing. */
  guests: z.array(z.preprocess(oneService, onlineService.extend({ clientId: z.string().uuid().nullable().default(null) }))).min(1).max(20),
  context: visitContext,
  /** Events: which date. */
  occurrenceId: z.string().uuid().nullable().default(null),
});

export const onlineBookingSchema = z.object({
  /** Marketing tracking cookies (only sent when the clinic has tracking on). */
  tracking: bookingTrackingSchema,
  bookerClientId: z.string().uuid(),
  start: localDateTimeSchema,
  guests: z
    .array(
      z.preprocess(
        oneService,
        onlineService.extend({
          /** Someone in the household, or a new person below. */
          clientId: z.string().uuid().nullable().default(null),
          person: onlinePersonSchema.nullable().default(null),
        }),
      ),
    )
    .min(1)
    .max(20)
    .refine((gs) => gs.every((g) => !!g.clientId !== !!g.person), "Each guest needs a name"),
  notes: z.string().trim().max(500).default(""),
  context: visitContext,
  mobile: z
    .object({
      address: mobileAddressSchema,
      locationType: z.string().trim().min(1).max(40),
      acknowledged: z.literal(true, { message: "Please check the acknowledgment" }),
      /** The kind of place has a notice (e.g. residences), and they ticked that they understand. */
      noticeAgreed: z.boolean().default(false),
      /** Outside the service area: they understand it may be declined. */
      outsideAgreed: z.boolean().default(false),
    })
    .nullable()
    .default(null),
  occurrenceId: z.string().uuid().nullable().default(null),
  answers: z.array(z.object({ label: z.string().max(120), answer: z.string().trim().max(300) })).max(5).default([]),
  /** Clinic visits: the client agreed to the card-on-file and no-show / late-cancel fee policy. */
  agreeToFees: z.boolean().default(false),
  /** The booking page's attempt (Reports → Unfinished bookings), marked finished. */
  attemptId: z.string().uuid().nullable().default(null),
  /** Clinic visits: use the booker's card on file instead of adding one. */
  useCardOnFile: z.boolean().default(false),
})
  .refine((v) => v.context !== "mobile" || !!v.mobile, { message: "Add the address for the visit", path: ["mobile"] })
  .refine((v) => v.context !== "event" || !!v.occurrenceId, { message: "Pick a date", path: ["occurrenceId"] });
export type OnlineBookingInput = z.infer<typeof onlineBookingSchema>;

export interface OnlinePerson {
  id: string;
  firstName: string;
  lastName: string;
  minor: boolean;
  /** No birth date on file (some imported clients): asked for before they book. */
  needsDob: boolean;
  /** Services from their last visit, to suggest first. */
  lastServiceIds: string[];
  /** Their last visit's services and add-ons ("Book again"). */
  lastVisit: { serviceId: string; addOnIds: string[] }[];
  /** Memberships and packages they have (active or on hold), by plan. */
  planIds: string[];
  /** The card on file (most recent), used for the no-show fee unless they add another. */
  card: { brand: string; last4: string } | null;
  /** Account credit (cents): applied when their visit is checked out. */
  creditCents: number;
}

export interface OnlineFamilyMember {
  id: string;
  firstName: string;
  /** Initial only, e.g. "S." */
  lastName: string;
}

export interface OnlineMe {
  /** Masked, e.g. (•••) •••-0101 */
  phone: string;
  people: OnlinePerson[];
  /**
   * Adults linked as family to an adult here: bookable as guests, but nothing else about them is
   * shared (last name is an initial; no visits, plans, or card). Never children: only their parent
   * or guardian books for them.
   */
  family: OnlineFamilyMember[];
  /** Saved addresses of the household, for mobile visits. */
  addresses: { label: string; text: string }[];
  /** The household's upcoming visits. */
  upcoming: { reference: string; start: string; firstName: string; serviceName: string; status: string }[];
}

export interface OnlineService {
  id: string;
  name: string;
  description: string;
  category: string;
  priceCents: number;
  minutes: number;
  /** Add-ons that come with it (no charge). */
  includedAddOnIds: string[];
  /** Add-ons that can be added to it. */
  addOnIds: string[];
  /** Offered in the clinic. */
  clinic: boolean;
  /** Mobile price, or null when not offered for mobile visits. */
  mobilePriceCents: number | null;
  /** Its category allows one per person per visit (one drip). */
  onePerVisit: boolean;
  /** Medications in it, so nobody books the same one twice. */
  medicationIds: string[];
}

export interface OnlineAddOn {
  id: string;
  name: string;
  description: string;
  priceCents: number;
  mobilePriceCents: number | null;
  /** Offered in the clinic. */
  clinic: boolean;
  minutes: number;
  medicationIds: string[];
}

export interface OnlineClinic {
  clinic: { name: string; phone: string | null; logoUrl: string | null; primaryColor: string; accentColor: string };
  /** Marketing tags to load on this page; null when tracking is off. */
  tracking: PublicTracking | null;
  timezone: string;
  /** First and last bookable dates. */
  window: { first: string; last: string };
  minNoticeMinutes: number;
  /** Clinic visits wait for the client to reply C to their text. */
  confirmByText: boolean;
  depositCents: number | null;
  cancellation: { windowHours: number; policy: string };
  /** Clinic visits save a card; no-shows and late cancellations are charged this fee. */
  /** optional: booking doesn't wait for a card (one can be added after booking). */
  cardOnFile: { feeCents: number; agreement: string; optional: boolean } | null;
  /** Deposit rules, so the review step can show the amount. */
  deposits: { mobile: DepositRule; event: DepositRule };
  groupDiscount: string | null;
  /** Service categories in menu order (with something bookable online). */
  categories: { name: string; description: string }[];
  services: OnlineService[];
  addOns: OnlineAddOn[];
  /** Only when the clinic lets clients choose their nurse. */
  nurses: { id: string; name: string }[] | null;
  /** Mobile visits, when any service is offered for mobile. */
  mobile: {
    window: { first: string; last: string };
    leadTimeMinutes: number;
    /** notice: shown when picked; the client ticks that they understand. */
    locationTypes: { key: string; label: string; notice: string }[];
    /** Mobile bookings need at least this many guests. */
    minGuests: number;
    acknowledgment: string;
    /** Requests wait for a nurse to accept. */
    requiresConfirmation: boolean;
    groupDiscount: string | null;
  } | null;
  me: OnlineMe | null;
  /** Memberships and packages sold online (shown as separate options). */
  plans: OnlinePlanSummary[];
}

export interface OnlinePlanSummary {
  id: string;
  kind: "membership" | "package";
  name: string;
  description: string;
  priceCents: number;
  benefits: string[];
  /** Services its vouchers cover (to book the first visit right after buying). */
  voucherServiceIds: string[];
}

/** The next clinic opening if booked online now (lobby display, website). */
export interface NextOpening {
  /** Bookable within 15 minutes. */
  now: boolean;
  at: string | null;
  /** "Immediate availability", "Next available appt: Tomorrow at 6:00 AM"; null when nothing is open this week. */
  label: string | null;
}

export interface OnlineSlots {
  times: string[];
  /** Each open time: when each guest starts ("09:00", "09:15"), in guest order. */
  starts: Record<string, string[]>;
  prices: PriceLine[];
}

/** A public event date (active, listed, not cancelled or over), offered on the booking page's day strip. */
export interface OnlineEventDay {
  date: string;
  name: string;
  /** The venue's name ("NOUN Hotel"). */
  venue: string;
  /** Its street address ("542 South University Blvd, Norman, OK 73069"), or "" if none is set. */
  address: string;
  start: string;
  end: string;
  /** The event's own page, e.g. "/noun-hotel". */
  url: string;
  walkUpsOnly: boolean;
}

export interface OnlineConfirmation {
  reference: string;
  /** Mobile requests wait for a nurse; "pending" until the card is saved or the deposit paid. */
  status: "pending" | "confirmed" | "requested";
  /** What the client still needs to do: save a card, or pay (or authorize) the deposit. */
  payment: { kind: "save_card" | "deposit_hold" | "deposit"; amountCents: number; session: PaymentSession } | null;
  /** Card on file is optional and they have none: a link to add one now, for faster checkout. */
  addCardLink?: string | null;
  depositStatus: "none" | "due" | "held" | "paid" | "released" | "forfeited" | "refunded";
  travelFeeCents: number;
  start: string;
  guests: {
    /** Matches the booking status's forms list. */
    appointmentId: string;
    name: string;
    serviceName: string;
    addOns: string[];
    start: string;
    priceCents: number;
    /** A membership or package voucher pays for the service (the plan's name). */
    coveredBy: string | null;
    /** Forms still to sign, and the private link (opened with the guest's date of birth). */
    formsNeeded: number;
    formsLink: string;
    /** A health screening (good faith exam) follows the forms on that link. */
    screening: boolean;
    /** Their account credit that will go toward this visit at checkout (signed-in household only). */
    creditCents: number;
  }[];
  totalCents: number;
  depositCents: number | null;
}

/** (•••) •••-0101 */
export const maskPhone = (e164: string) => `(•••) •••-${e164.slice(-4)}`;

/** A membership or package on its online sign-up page (/join/<clinic>/<plan>). */
export interface OnlinePlan {
  id: string;
  kind: "membership" | "package";
  name: string;
  description: string;
  priceCents: number;
  benefits: string[];
  minimumMonths: number;
  noticeDays: number;
  agreementText: string;
  /** Archived, or not sold online. */
  available: boolean;
  /** Services its vouchers cover (to book the first visit right after buying). */
  voucherServiceIds: string[];
}

export const onlineJoinSchema = z.object({
  clientId: z.string().uuid(),
  signedName: z.string().trim().max(120).default(""),
  agreed: z.boolean().default(false),
});

// ── Website feed (public, read-only): what the clinic's own website shows, kept in step with the app.

/** A clinic's website feed: GET /v1/public/clinics/<link name>/website. Only what's public; no client data. Links are absolute. */
export interface WebsiteFeed {
  updatedAt: string;
  clinic: {
    name: string;
    tagline: string | null;
    phone: string | null;
    email: string | null;
    address: string | null;
    hours: string | null;
    website: string | null;
    instagram: string | null;
    facebook: string | null;
    logoUrl: string | null;
    primaryColor: string;
    accentColor: string;
    /** Online booking (clinic visits); mobile booking when the clinic offers it. */
    bookingUrl: string;
    mobileBookingUrl: string | null;
  };
  categories: { name: string; description: string }[];
  services: WebsiteService[];
  addOns: WebsiteAddOn[];
  /** Memberships (monthly) and packages sold online, each with its sign-up page. */
  plans: { id: string; kind: "membership" | "package"; name: string; description: string; priceCents: number; benefits: string[]; signUpUrl: string }[];
  events: { name: string; venue: string; city: string; dates: { start: string; end: string; title: string }[]; url: string; imageUrl: string | null }[];
  staff: { id: string; name: string; credentials: string; title: string; bio: string; photoUrl: string | null }[];
  /** The clinic's cancellation policy, as on the booking page. */
  cancellationPolicy: string;
}

export interface WebsiteService {
  id: string;
  name: string;
  description: string;
  category: string;
  priceCents: number;
  /** Null when it isn't offered mobile. */
  mobilePriceCents: number | null;
  inClinic: boolean;
  minutes: number;
  /** What's in it, from the recipe ("NAD+ 500 mg", "B-Complex 1 mL"). */
  ingredients: string[];
  addOnIds: string[];
  photoUrl: string | null;
  /** Opens online booking with this service picked. */
  bookUrl: string;
}

export interface WebsiteAddOn {
  id: string;
  name: string;
  description: string;
  priceCents: number;
  mobilePriceCents: number | null;
  ingredients: string[];
  photoUrl: string | null;
}
